import React, { useState, useEffect, useCallback } from 'react'
import { useNavigate, useSearchParams, Outlet, useMatch } from 'react-router-dom'
import { pesquisarClientesPaginado, getTamanhoPaginacao } from '../../lib/supabase'
import { useTenant } from '../../contexts/TenantContext'
import { useToast } from '../../contexts/ToastContext'
import Loading from '../../components/Loading'
import { useResponsive } from '../../hooks/useResponsive'

const inputStyle = {
  width: '100%', padding: '9px 12px',
  border: '1px solid var(--cor-borda)', borderRadius: 'var(--raio)',
  fontSize: '14px', fontFamily: 'var(--font-ui)',
  background: 'var(--cor-branco)', color: 'var(--cor-texto)',
}

const contactoLink = {
  color: 'inherit',
  textDecoration: 'none',
  whiteSpace: 'nowrap',
}

function formatarData(iso) {
  if (!iso) return '—'
  const d = new Date(iso)
  return d.toLocaleDateString('pt-PT', { day: '2-digit', month: '2-digit', year: 'numeric' })
}

export default function Clientes() {
  const { tenant } = useTenant()
  const toast = useToast()
  const navigate = useNavigate()
  const { isDesktop } = useResponsive()
  // Rota filha /admin/clientes/:id activa — mostra-se em painel lateral (desktop)
  // ou em popup de ecrã inteiro (ecrãs mais estreitos), sem sair desta página.
  const matchDetalhe = useMatch('/admin/clientes/:id')
  const detalheAberto = !!matchDetalhe
  const idSelecionado = matchDetalhe?.params?.id

  // Estado dos filtros/página persistido no URL, para sobreviver à ida ao
  // detalhe do cliente e volta (o botão "voltar" mantém estes query params).
  const [searchParams, setSearchParams] = useSearchParams()
  const pesquisaUrl = searchParams.get('q') || ''
  const apenasAtivos = searchParams.get('ativos') !== '0'
  const pagina = Math.max(0, parseInt(searchParams.get('pag'), 10) || 0)

  const [lista, setLista] = useState([])
  const [total, setTotal] = useState(0)
  const [carregando, setCarregando] = useState(true)
  const [tamanhoPagina, setTamanhoPagina] = useState(20)

  // Campo de pesquisa controlado localmente (com debounce antes de escrever no URL)
  const [pesquisa, setPesquisa] = useState(pesquisaUrl)

  const atualizarParams = useCallback((patch) => {
    setSearchParams(prev => {
      const p = new URLSearchParams(prev)
      Object.entries(patch).forEach(([k, v]) => {
        if (v === null || v === undefined || v === '') p.delete(k)
        else p.set(k, v)
      })
      return p
    }, { replace: true })
  }, [setSearchParams])

  // Debounce: escreve a pesquisa no URL e volta à primeira página
  useEffect(() => {
    if (pesquisa === pesquisaUrl) return
    const timer = setTimeout(() => {
      atualizarParams({ q: pesquisa || null, pag: null })
    }, 300)
    return () => clearTimeout(timer)
  }, [pesquisa, pesquisaUrl, atualizarParams])

  // Tamanho de página configurável em Config Admin (fallback a 20).
  useEffect(() => {
    if (!tenant) return
    getTamanhoPaginacao(tenant.id).then(setTamanhoPagina).catch(() => {})
  }, [tenant])

  // Carrega a página sempre que tenant, filtros, página ou tamanho de página mudam
  useEffect(() => {
    if (!tenant) return
    let cancelado = false
    setCarregando(true)
    pesquisarClientesPaginado(tenant.id, pesquisaUrl, pagina, apenasAtivos, tamanhoPagina)
      .then(({ dados, total }) => {
        if (cancelado) return
        setLista(dados)
        setTotal(total)
      })
      .catch(e => { if (!cancelado) toast.erro(e.message) })
      .finally(() => { if (!cancelado) setCarregando(false) })
    return () => { cancelado = true }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenant, pesquisaUrl, apenasAtivos, pagina, tamanhoPagina])

  const totalPaginas = Math.max(1, Math.ceil(total / tamanhoPagina))
  const inicio = total === 0 ? 0 : pagina * tamanhoPagina + 1
  const fim = Math.min(total, (pagina + 1) * tamanhoPagina)

  function irParaPagina(p) {
    const alvo = Math.min(Math.max(0, p), totalPaginas - 1)
    atualizarParams({ pag: alvo === 0 ? null : alvo })
  }

  return (
    <div style={{ maxWidth: isDesktop ? '1400px' : '760px', margin: '0 auto', padding: isDesktop && detalheAberto ? '24px 24px 0' : '32px 24px 64px' }}>
    <div style={isDesktop ? (
      detalheAberto
        ? { display: 'grid', gridTemplateColumns: '380px minmax(0, 1fr)', gap: '20px', alignItems: 'stretch', height: 'calc(100vh - 24px)' }
        : { display: 'block' }
    ) : undefined}>
    {/* Coluna da lista: no desktop com detalhe aberto rola de forma independente
        do detalhe (layout tipo lista+detalhe); caso contrário rola com a página. */}
    <div style={isDesktop && detalheAberto ? { overflowY: 'auto', paddingRight: '4px', paddingBottom: '24px' } : undefined}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px', flexWrap: 'wrap', gap: '12px' }}>
        <h2 style={{ fontSize: '22px' }}>Clientes</h2>
        <button
          className="btn-primario"
          onClick={() => navigate('/admin/clientes/novo')}
          style={{ padding: '8px 18px', fontSize: '13px' }}
        >
          + Novo Cliente
        </button>
      </div>
      <p style={{ color: 'var(--cor-texto-suave)', marginBottom: '20px', fontSize: '14px' }}>
        Informações e histórico de marcações dos clientes do centro.
      </p>

      {/* Pesquisa + filtro Ativos */}
      <div style={{ display: 'flex', gap: '12px', marginBottom: '20px', alignItems: 'center', flexWrap: 'wrap' }}>
        <input
          type="search"
          value={pesquisa}
          onChange={e => setPesquisa(e.target.value)}
          placeholder="Pesquisar por nome, telefone ou email…"
          style={{ ...inputStyle, flex: 1, minWidth: '200px' }}
        />
        <label style={{ display: 'flex', alignItems: 'center', gap: '7px', fontSize: '13px', cursor: 'pointer', whiteSpace: 'nowrap', userSelect: 'none' }}>
          <input
            type="checkbox"
            checked={apenasAtivos}
            onChange={e => atualizarParams({ ativos: e.target.checked ? null : '0', pag: null })}
            style={{ width: '15px', height: '15px', accentColor: 'var(--cor-principal)', cursor: 'pointer' }}
          />
          Apenas ativos
        </label>
      </div>

      {carregando ? <Loading /> : (
        <>
          {lista.length === 0 && (
            <p style={{ color: 'var(--cor-texto-suave)', textAlign: 'center', padding: '32px' }}>
              {pesquisaUrl ? 'Nenhum cliente encontrado.' : 'Sem clientes registados.'}
            </p>
          )}

          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {lista.map(cliente => (
              <button
                key={cliente.id}
                onClick={() => navigate(`/admin/clientes/${cliente.id}`)}
                style={{
                  background: 'var(--cor-branco)', border: '1px solid var(--cor-borda)',
                  borderRadius: 'var(--raio-lg)', padding: '16px 20px',
                  display: 'flex', alignItems: 'center', gap: '14px', flexWrap: 'wrap',
                  boxShadow: 'var(--sombra)', cursor: 'pointer', textAlign: 'left', width: '100%',
                  transition: 'border-color 0.15s',
                  opacity: cliente.ativo ? 1 : 0.5,
                  outline: idSelecionado === cliente.id ? '2px solid var(--cor-principal)' : 'none',
                  outlineOffset: '-1px',
                }}
                onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--cor-principal)'}
                onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--cor-borda)'}
              >
                <div style={{
                  width: '42px', height: '42px', borderRadius: '50%',
                  background: cliente.ativo ? 'var(--cor-principal)' : 'var(--cor-texto-suave)',
                  color: 'white', fontSize: '17px', fontWeight: '700',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                }}>
                  {(cliente.nome_amigavel || cliente.nome || '?').charAt(0).toUpperCase()}
                </div>

                <div style={{ flex: 1, minWidth: '160px' }}>
                  <div style={{ fontWeight: '600', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    {cliente.nome_amigavel || cliente.nome}
                    {cliente.nome_amigavel && cliente.nome_amigavel !== cliente.nome && (
                      <span style={{ fontWeight: '400', fontSize: '11px', color: 'var(--cor-texto-suave)' }}>
                        ({cliente.nome})
                      </span>
                    )}
                    {!cliente.ativo && (
                      <span style={{ fontSize: '10px', background: '#F3F4F6', color: '#6B7280', padding: '1px 6px', borderRadius: '999px', fontWeight: '500' }}>
                        Inativo
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: '12px', color: 'var(--cor-texto-suave)', marginTop: '2px', display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                    {cliente.telefone && (
                      <a href={`tel:${cliente.telefone}`} style={contactoLink} onClick={e => e.stopPropagation()}>
                        📞 {cliente.telefone}
                      </a>
                    )}
                    {cliente.email && (
                      <a href={`mailto:${cliente.email}`} style={contactoLink} onClick={e => e.stopPropagation()}>
                        ✉ {cliente.email}
                      </a>
                    )}
                    {cliente.data_nascimento && <span>🎂 {formatarData(cliente.data_nascimento + 'T12:00:00')}</span>}
                  </div>
                  {cliente.observacoes && (
                    <div style={{ fontSize: '12px', color: 'var(--cor-texto-suave)', marginTop: '4px', fontStyle: 'italic', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '360px' }}>
                      {cliente.observacoes}
                    </div>
                  )}
                </div>

                <span style={{ fontSize: '16px', color: 'var(--cor-texto-suave)', flexShrink: 0 }}>›</span>
              </button>
            ))}
          </div>

          {total > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '20px', flexWrap: 'wrap', gap: '12px' }}>
              <span style={{ fontSize: '13px', color: 'var(--cor-texto-suave)' }}>
                {inicio}–{fim} de {total}
              </span>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <button
                  onClick={() => irParaPagina(pagina - 1)}
                  disabled={pagina === 0}
                  className="btn-secundario"
                  style={{ padding: '8px 18px', fontSize: '13px', opacity: pagina === 0 ? 0.5 : 1, cursor: pagina === 0 ? 'default' : 'pointer' }}
                >
                  ‹ Anterior
                </button>
                <span style={{ fontSize: '13px', color: 'var(--cor-texto-suave)', whiteSpace: 'nowrap' }}>
                  Página {pagina + 1} de {totalPaginas}
                </span>
                <button
                  onClick={() => irParaPagina(pagina + 1)}
                  disabled={pagina + 1 >= totalPaginas}
                  className="btn-secundario"
                  style={{ padding: '8px 18px', fontSize: '13px', opacity: pagina + 1 >= totalPaginas ? 0.5 : 1, cursor: pagina + 1 >= totalPaginas ? 'default' : 'pointer' }}
                >
                  Seguinte ›
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>

    {isDesktop && (
      // Coluna do detalhe: scroll próprio, independente da lista.
      <div style={{ overflowY: 'auto', paddingBottom: '24px' }}>
        {detalheAberto ? <Outlet /> : (
          <div style={{ background: 'var(--cor-branco)', border: '1px solid var(--cor-borda)', borderRadius: 'var(--raio-lg)', boxShadow: 'var(--sombra)', padding: '64px 24px', textAlign: 'center', color: 'var(--cor-texto-suave)' }}>
            <p style={{ fontSize: '40px', marginBottom: '12px' }}>👤</p>
            Seleciona um cliente da lista para ver os detalhes.
          </div>
        )}
      </div>
    )}
    </div>

    {/* Em ecrãs mais estreitos não há espaço para as duas colunas — o detalhe
        abre em popup de ecrã inteiro por cima da lista, em vez de navegar
        para outra página (a lista fica preservada por baixo). */}
    {!isDesktop && detalheAberto && (
      <div style={{ position: 'fixed', inset: 0, background: 'var(--cor-creme)', zIndex: 45, overflowY: 'auto' }}>
        <Outlet />
      </div>
    )}
    </div>
  )
}
